import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";

import { getIdToken, getJwtToken } from "../common/utils/getJwtToken";

import { RestLink } from "apollo-link-rest";
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link";
import { defaultDataIdFromObject } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { appSyncConfig } from "./reexports";

const url = appSyncConfig.aws_appsync_graphqlEndpoint;
const region = appSyncConfig.aws_appsync_region;

const CACHE_DURATION = 30 * 60 * 1000;

const freshnessTimestamps = {};

const cacheControlLink = new ApolloLink((operation, forward) => {
  const key = operation.operationName;
  const lastFetched = freshnessTimestamps[key];
  const now = Date.now();

  // Always set a consistent policy based on freshness
  if (!lastFetched || now - lastFetched > CACHE_DURATION) {
    // Stale or never fetched - get from cache IMMEDIATELY but also refresh from network
    operation.setContext({
      fetchPolicy: "cache-and-network",
      // Keep using cache-first for subsequent requests within this session
      nextFetchPolicy: "cache-first",
    });
  } else {
    // Fresh data - use cache only
    operation.setContext({
      fetchPolicy: "cache-first",
    });
  }

  return forward(operation).map(result => {
    // Update timestamp when we get a result
    freshnessTimestamps[key] = Date.now();

    return result;
  });
});

const httpLink = new HttpLink({
  uri: url,
});

// Remove trailing slash from the API URL to prevent double slashes.
// This is a temporary fix for debugging. The permanent fix should be in the CDK.
const eventDataApiUrl = appSyncConfig.event_data_api_url?.replace(/\/$/, "");

const restLink = eventDataApiUrl
  ? new RestLink({
      uri: eventDataApiUrl,
    })
  : null;

const authLink = setContext(async (context, { headers }) => {
  const acessToken = await getJwtToken();
  const idToken = await getIdToken();

  return {
    headers: {
      ...headers,
      Authorization: `Bearer ${acessToken}`,
      "id-token": idToken,
    },
  };
});

const restAuthLink = setContext(async (_, { headers }) => {
  const token = await getJwtToken();
  const idToken = await getIdToken();

  return {
    headers: {
      ...headers,
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "id-token": idToken,
    },
  };
});

const auth = {
  type: appSyncConfig.aws_appsync_authenticationType,
  jwtToken: async () => await getJwtToken(),
};

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.error(
        `[GraphQL error]: Message: ${message}, Path: ${path}, Location:`,
        locations
      )
    );
  }

  if (networkError) {
    console.error(`[Network error]:`, {
      message: networkError.message,
      name: networkError.name,
      operationName: operation.operationName,
    });
  }
});

const subscriptionLink = createSubscriptionHandshakeLink(
  {
    url,
    region,
    auth,
  },
  httpLink
);

const restLinkWithAuthOrDefault = restLink
  ? [restAuthLink, restLink]
  : [restAuthLink];

const splitLink = ApolloLink.split(
  operation => {
    const mainDef = operation.query.definitions.find(
      def => def.kind === "OperationDefinition"
    );

    return (
      mainDef &&
      "selectionSet" in mainDef &&
      mainDef.selectionSet.selections.some(
        selection =>
          "directives" in selection &&
          selection.directives?.some(d => d.name.value === "rest")
      )
    );
  },
  ApolloLink.from(restLinkWithAuthOrDefault),
  ApolloLink.from([authLink, subscriptionLink])
);

const link = ApolloLink.from([errorLink, cacheControlLink, splitLink]);

const getMergedItems = refKey => {
  return {
    // NOTE: Don't cache separate results based on any of this field's arguments.
    keyArgs: false,
    merge(existing, incoming, { variables, cache }) {
      if (!incoming) return existing;

      if (!existing) return incoming; // existing will be empty the first time

      // eslint-disable-next-line
      const { items, ...rest } = incoming;

      if (refKey) {
        const refValue = variables[refKey];

        if (refValue) {
          const existingItem = existing?.items
            ? existing?.items[existing?.items?.length - 1]
            : existing[existing.length - 1];
          const resetCache = existingItem?.__ref.indexOf(refValue) === -1;

          if (resetCache) {
            cache.reset();

            return incoming;
          }

          if (existing.items.length && incoming.length === 0) {
            return incoming;
          }
        }
      }

      // NOTE: fixed duplication issues when user goes trough navigation and back to Locations page
      // this a quick solution, bad performance for >10k items
      // possible solution: https://stackoverflow.com/a/68734886/6772862
      const ids = new Set(existing.items.map(d => d.__ref));
      const merged = [
        ...existing.items,
        ...incoming.items.filter(d => !ids.has(d.__ref)),
      ];

      const result = {
        ...rest,
        items: merged,
      };

      return result;
    },
  };
};

// A fetchMore query doesn't automatically merge its result with the original query's cached result
// so we need to do it manually. This is a custom merge function.
const mergeCache = (existing = { items: [] }, incoming) => {
  if (!incoming?.items?.length) return existing;

  const existingItems = existing.items ?? [];
  const incomingItems = incoming.items ?? [];

  const ids = new Set(existingItems.map(item => item.__ref));

  const mergedItems = [
    ...existingItems,
    ...incomingItems.filter(i => !ids.has(i.__ref)),
  ];

  return {
    ...incoming,
    items: mergedItems,
  };
};

const client = new ApolloClient({
  link,
  cache: new InMemoryCache({
    dataIdFromObject(responseObject) {
      switch (responseObject.__typename) {
        case "Device":
          // Ensure consistent ID for devices regardless of how they're fetched
          return `Device:${responseObject.id}`;
        case "Zone":
          return `Zone:${responseObject.deviceId}#${responseObject.id}`;
        case "Customer":
          return `Customer:${responseObject.externalId}`;
        default:
          return defaultDataIdFromObject(responseObject);
      }
    },
    typePolicies: {
      Query: {
        fields: {
          getAllWildCardsForSubscriptionEntity: getMergedItems(),
          // there is an issue with merging for annotations, where changing query params still shows old results; commenting out merge policy for now
          // getAnnotations: getMergedItems("serviceId"),
          getZones: getMergedItems("serviceId"),
          getHumanValidatedEventsForAuditing: getMergedItems("customerId"),
          getDTCustomerProjects: getMergedItems("customerId"),
          getUsersByCompany: getMergedItems("customerId"),
          getUserAlerts: getMergedItems(),
          getNodes: {
            keyArgs: ["locationId"],
            merge(existing, incoming, { variables }) {
              if (!incoming) return existing;

              if (!variables?.nextToken) return incoming;

              return mergeCache(existing ?? { items: [] }, incoming);
            },
          },

          getDevices: {
            keyArgs: ["customerId", "locationId"],
            merge: mergeCache,
          },
          getDevicesMakes: {
            keyArgs: false,
            merge: mergeCache,
          },
          getAllAlerts: {
            keyArgs: ["customerId", "location", "model"],
            merge: mergeCache,
          },
          getLocations: {
            keyArgs: ["customerId"],
            merge: mergeCache,
          },
          getCategoriesByCustomer: {
            keyArgs: ["customerId"],
            merge: mergeCache,
          },
          getLicensePlatesByCustomer: {
            keyArgs: ["customerId"],
            merge: mergeCache,
          },
          getServices: {
            keyArgs: ["customerId"],
            merge: mergeCache,
          },
          getServicesByNode: {
            keyArgs: ["nodeId"],
            merge: mergeCache,
          },
          getNodesByCustomer: {
            keyArgs: ["customerId"],
            merge(existing, incoming, { readField }) {
              if (!existing) return incoming;

              if (!incoming) return existing;

              // Properly merge items and preserve nested devices
              const mergedItems = [...(existing.items || [])];
              const incomingItems = incoming.items || [];

              incomingItems.forEach(incomingItem => {
                const incomingId = readField("id", incomingItem);
                const existingItemIndex = mergedItems.findIndex(
                  existingItem => readField("id", existingItem) === incomingId
                );

                if (existingItemIndex >= 0) {
                  // Preserve the devices array when merging
                  const existingDevices =
                    readField("devices", mergedItems[existingItemIndex]) || [];
                  const incomingDevices =
                    readField("devices", incomingItem) || [];

                  // Create new merged item with preserved devices
                  mergedItems[existingItemIndex] = {
                    ...incomingItem,
                    devices: [...existingDevices, ...incomingDevices],
                  };
                } else {
                  mergedItems.push(incomingItem);
                }
              });

              return {
                ...incoming,
                items: mergedItems,
              };
            },
          },

          events: {
            keyArgs: ["customerId", "facilityId", "modelId"],
          },
        },
      },
      Node: {
        fields: {
          devices: {
            // This automatically handles merging devices arrays for CRUD operations
            merge(existing, incoming, { readField }) {
              // Ensure we always have arrays even if null values are passed
              const safeExisting = existing || [];
              const safeIncoming = incoming || [];

              // Create a map of existing devices by ID for quick lookup
              const existingDevicesMap = new Map();

              safeExisting.forEach(device => {
                const deviceId = readField("id", device);

                if (deviceId) existingDevicesMap.set(deviceId, device);
              });

              // Merge incoming devices, preferring new data for existing devices
              const merged = [...safeExisting]; // Start with existing devices

              safeIncoming.forEach(device => {
                const deviceId = readField("id", device);

                if (!deviceId) return;

                const existingIndex = merged.findIndex(
                  item => readField("id", item) === deviceId
                );

                if (existingIndex >= 0) {
                  // Update existing device
                  merged[existingIndex] = device;
                } else {
                  // Add new device
                  merged.push(device);
                }
              });

              return merged;
            },
          },
        },
      },
    },
  }),
});

export default client;
